import axios, { AxiosResponse } from 'axios';
import { ADMIN_API_CONFIG } from '../config';
import {
  Category,
  CategoryCreate,
  CategoryUpdate,
  Penalty,
  PenaltyCreate,
  PenaltyUpdate,
  Estate,
  EstateCreate,
  EstateUpdate,
  Divisi,
  DivisiCreate,
  DivisiUpdate,
  Blok,
  BlokCreate,
  BlokUpdate,
  JobLevel,
  JobLevelCreate,
  JobLevelUpdate,
  ForemanGroup,
  ForemanGroupCreate,
  ForemanGroupUpdate,
  Employee,
  EmployeeCreate,
  EmployeeUpdate,
  BlokInspeksiResponse,
  BlokInspeksiQueryParams,
  SyncStatusUpdate,
  PaginatedResponse,
  QueryParams,
  LoginRequestQueryParams,
  LoginRequestResponse,
  LoginRequestApproval,
  BulkOperationResult,
  AdminPermissionResponse,
  AdminUserInfoResponse,
} from '../types';

class AdminApiService {
  private readonly token: string | null = null;

  constructor() {
    // Set up axios defaults
    axios.defaults.baseURL = ADMIN_API_CONFIG.BASE_URL;
    
    // Set up axios interceptors
    axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['Content-Type'] = 'application/json';
        return config;
      },
      (error) => {
        return Promise.reject(new Error(error.message || 'Request interceptor error'));
      }
    );

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/admin/login';
        }
        return Promise.reject(new Error(error.response?.data?.message || error.message || 'Response error'));
      }
    );

    this.token = localStorage.getItem('token');
  }

  // ===== ADMIN PERMISSION CHECK =====

  async checkAdminPermission(): Promise<AdminPermissionResponse> {
    const response: AxiosResponse<AdminPermissionResponse> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.CHECK_PERMISSION);
    return response.data;
  }

  async getAdminUserInfo(): Promise<AdminUserInfoResponse> {
    const response: AxiosResponse<AdminUserInfoResponse> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.USER_INFO);
    return response.data;
  }

  // ===== CATEGORIES =====

  async getCategories(params?: QueryParams): Promise<PaginatedResponse<Category>> {
    const queryString = this.buildQueryString(params);
    const response: AxiosResponse<PaginatedResponse<Category>> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.CATEGORIES}${queryString}`);
    return response.data;
  }

  async getCategory(id: number): Promise<Category> {
    const response: AxiosResponse<Category> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.CATEGORY_BY_ID(id));
    return response.data;
  }

  async createCategory(data: CategoryCreate): Promise<Category> {
    const response: AxiosResponse<Category> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.CATEGORIES, data);
    return response.data;
  }

  async updateCategory(id: number, data: CategoryUpdate): Promise<Category> {
    const response: AxiosResponse<Category> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.CATEGORY_BY_ID(id), data);
    return response.data;
  }

  async deleteCategory(id: number): Promise<Category> {
    const response: AxiosResponse<Category> = await axios.delete(ADMIN_API_CONFIG.ENDPOINTS.CATEGORY_BY_ID(id));
    return response.data;
  }

  async restoreCategory(id: number): Promise<Category> {
    const response: AxiosResponse<Category> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.CATEGORY_RESTORE(id));
    return response.data;
  }

  // ===== PENALTIES =====

  async getPenalties(params?: QueryParams): Promise<PaginatedResponse<Penalty>> {
    const queryString = this.buildQueryString(params);
    const response: AxiosResponse<PaginatedResponse<Penalty>> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.PENALTIES}${queryString}`);
    return response.data;
  }

  async getPenalty(id: number): Promise<Penalty> {
    const response: AxiosResponse<Penalty> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.PENALTY_BY_ID(id));
    return response.data;
  }

  async createPenalty(data: PenaltyCreate): Promise<Penalty> {
    const response: AxiosResponse<Penalty> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.PENALTIES, data);
    return response.data;
  }

  async updatePenalty(id: number, data: PenaltyUpdate): Promise<Penalty> {
    const response: AxiosResponse<Penalty> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.PENALTY_BY_ID(id), data);
    return response.data;
  }

  async deletePenalty(id: number): Promise<Penalty> {
    const response: AxiosResponse<Penalty> = await axios.delete(ADMIN_API_CONFIG.ENDPOINTS.PENALTY_BY_ID(id));
    return response.data;
  }

  async restorePenalty(id: number): Promise<Penalty> {
    const response: AxiosResponse<Penalty> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.PENALTY_RESTORE(id));
    return response.data;
  }

  // ===== ESTATES =====

  async getEstates(params?: QueryParams): Promise<PaginatedResponse<Estate>> {
    const queryString = this.buildQueryString(params);
    const response: AxiosResponse<PaginatedResponse<Estate>> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.ESTATES}${queryString}`);
    return response.data;
  }

  async getEstate(id: number): Promise<Estate> {
    const response: AxiosResponse<Estate> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.ESTATE_BY_ID(id));
    return response.data;
  }

  async createEstate(data: EstateCreate): Promise<Estate> {
    const response: AxiosResponse<Estate> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.ESTATES, data);
    return response.data;
  }

  async updateEstate(id: number, data: EstateUpdate): Promise<Estate> {
    const response: AxiosResponse<Estate> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.ESTATE_BY_ID(id), data);
    return response.data;
  }

  async deleteEstate(id: number): Promise<Estate> {
    const response: AxiosResponse<Estate> = await axios.delete(ADMIN_API_CONFIG.ENDPOINTS.ESTATE_BY_ID(id));
    return response.data;
  }

  async restoreEstate(id: number): Promise<Estate> {
    const response: AxiosResponse<Estate> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.ESTATE_RESTORE(id));
    return response.data;
  }

  // ===== DIVISI =====

  async getDivisis(params?: QueryParams): Promise<PaginatedResponse<Divisi>> {
    const queryString = this.buildQueryString(params);
    const response: AxiosResponse<PaginatedResponse<Divisi>> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.DIVISIS}${queryString}`);
    return response.data;
  }

  async getDivisi(id: number): Promise<Divisi> {
    const response: AxiosResponse<Divisi> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.DIVISI_BY_ID(id));
    return response.data;
  }

  async createDivisi(data: DivisiCreate): Promise<Divisi> {
    const response: AxiosResponse<Divisi> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.DIVISIS, data);
    return response.data;
  }

  async updateDivisi(id: number, data: DivisiUpdate): Promise<Divisi> {
    const response: AxiosResponse<Divisi> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.DIVISI_BY_ID(id), data);
    return response.data;
  }

  async deleteDivisi(id: number): Promise<Divisi> {
    const response: AxiosResponse<Divisi> = await axios.delete(ADMIN_API_CONFIG.ENDPOINTS.DIVISI_BY_ID(id));
    return response.data;
  }

  async restoreDivisi(id: number): Promise<Divisi> {
    const response: AxiosResponse<Divisi> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.DIVISI_RESTORE(id));
    return response.data;
  }

  // ===== BLOKS =====

  async getBloks(params?: QueryParams): Promise<PaginatedResponse<Blok>> {
    const queryString = this.buildQueryString(params);
    const response: AxiosResponse<PaginatedResponse<Blok>> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.BLOKS}${queryString}`);
    return response.data;
  }

  async getBlok(id: number): Promise<Blok> {
    const response: AxiosResponse<Blok> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.BLOK_BY_ID(id));
    return response.data;
  }

  async createBlok(data: BlokCreate): Promise<Blok> {
    const response: AxiosResponse<Blok> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.BLOKS, data);
    return response.data;
  }

  async updateBlok(id: number, data: BlokUpdate): Promise<Blok> {
    const response: AxiosResponse<Blok> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.BLOK_BY_ID(id), data);
    return response.data;
  }

  async deleteBlok(id: number): Promise<Blok> {
    const response: AxiosResponse<Blok> = await axios.delete(ADMIN_API_CONFIG.ENDPOINTS.BLOK_BY_ID(id));
    return response.data;
  }

  async restoreBlok(id: number): Promise<Blok> {
    const response: AxiosResponse<Blok> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.BLOK_RESTORE(id));
    return response.data;
  }

  // ===== JOB LEVELS =====

  async getJobLevels(params?: QueryParams): Promise<PaginatedResponse<JobLevel>> {
    const queryString = this.buildQueryString(params);
    const response: AxiosResponse<PaginatedResponse<JobLevel>> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.JOB_LEVELS}${queryString}`);
    return response.data;
  }

  async getJobLevel(id: number): Promise<JobLevel> {
    const response: AxiosResponse<JobLevel> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.JOB_LEVEL_BY_ID(id));
    return response.data;
  }

  async createJobLevel(data: JobLevelCreate): Promise<JobLevel> {
    const response: AxiosResponse<JobLevel> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.JOB_LEVELS, data);
    return response.data;
  }

  async updateJobLevel(id: number, data: JobLevelUpdate): Promise<JobLevel> {
    const response: AxiosResponse<JobLevel> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.JOB_LEVEL_BY_ID(id), data);
    return response.data;
  }

  async deleteJobLevel(id: number): Promise<JobLevel> {
    const response: AxiosResponse<JobLevel> = await axios.delete(ADMIN_API_CONFIG.ENDPOINTS.JOB_LEVEL_BY_ID(id));
    return response.data;
  }

  async restoreJobLevel(id: number): Promise<JobLevel> {
    const response: AxiosResponse<JobLevel> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.JOB_LEVEL_RESTORE(id));
    return response.data;
  }

  // ===== FOREMAN GROUPS =====

  async getForemanGroups(params?: QueryParams): Promise<PaginatedResponse<ForemanGroup>> {
    const queryString = this.buildQueryString(params);
    const response: AxiosResponse<PaginatedResponse<ForemanGroup>> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.FOREMAN_GROUPS}${queryString}`);
    return response.data;
  }

  async getForemanGroup(id: number): Promise<ForemanGroup> {
    const response: AxiosResponse<ForemanGroup> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.FOREMAN_GROUP_BY_ID(id));
    return response.data;
  }

  async createForemanGroup(data: ForemanGroupCreate): Promise<ForemanGroup> {
    const response: AxiosResponse<ForemanGroup> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.FOREMAN_GROUPS, data);
    return response.data;
  }

  async updateForemanGroup(id: number, data: ForemanGroupUpdate): Promise<ForemanGroup> {
    const response: AxiosResponse<ForemanGroup> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.FOREMAN_GROUP_BY_ID(id), data);
    return response.data;
  }

  async deleteForemanGroup(id: number): Promise<ForemanGroup> {
    const response: AxiosResponse<ForemanGroup> = await axios.delete(ADMIN_API_CONFIG.ENDPOINTS.FOREMAN_GROUP_BY_ID(id));
    return response.data;
  }

  async restoreForemanGroup(id: number): Promise<ForemanGroup> {
    const response: AxiosResponse<ForemanGroup> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.FOREMAN_GROUP_RESTORE(id));
    return response.data;
  }

  // ===== EMPLOYEES =====

  async getEmployees(params?: QueryParams): Promise<PaginatedResponse<Employee>> {
    const queryString = this.buildQueryString(params);
    const response: AxiosResponse<PaginatedResponse<Employee>> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.EMPLOYEES}${queryString}`);
    return response.data;
  }

  async getEmployee(id: number): Promise<Employee> {
    const response: AxiosResponse<Employee> = await axios.get(ADMIN_API_CONFIG.ENDPOINTS.EMPLOYEE_BY_ID(id));
    return response.data;
  }

  async createEmployee(data: EmployeeCreate): Promise<Employee> {
    const response: AxiosResponse<Employee> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.EMPLOYEES, data);
    return response.data;
  }

  async updateEmployee(id: number, data: EmployeeUpdate): Promise<Employee> {
    const response: AxiosResponse<Employee> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.EMPLOYEE_BY_ID(id), data);
    return response.data;
  }

  async deleteEmployee(id: number): Promise<Employee> {
    const response: AxiosResponse<Employee> = await axios.delete(ADMIN_API_CONFIG.ENDPOINTS.EMPLOYEE_BY_ID(id));
    return response.data;
  }

  async restoreEmployee(id: number): Promise<Employee> {
    const response: AxiosResponse<Employee> = await axios.post(ADMIN_API_CONFIG.ENDPOINTS.EMPLOYEE_RESTORE(id));
    return response.data;
  }

  // ===== BLOK INSPEKSI =====

  async getInspeksiList(params?: BlokInspeksiQueryParams): Promise<BlokInspeksiResponse> {
    const queryString = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    const response: AxiosResponse<BlokInspeksiResponse> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.BLOK_INSPEKSI_LIST}${queryString}`);
    return response.data;
  }

  async updateBlokInspeksiSyncStatus(uuid: string, data: SyncStatusUpdate): Promise<SyncStatusUpdate> {
    const response: AxiosResponse<SyncStatusUpdate> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.BLOK_INSPEKSI_SYNC_STATUS(uuid), data);
    return response.data;
  }

  // ===== LOGIN REQUESTS =====

  async getLoginRequests(params?: LoginRequestQueryParams): Promise<LoginRequestResponse> {
    const queryString = this.buildLoginRequestQueryString(params);
    const response: AxiosResponse<LoginRequestResponse> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.LOGIN_REQUESTS}${queryString}`);
    return response.data;
  }

  async getPendingLoginRequests(params?: { limit?: number; offset?: number }): Promise<LoginRequestResponse> {
    const queryString = params ? this.buildLoginRequestQueryString(params) : '';
    const response: AxiosResponse<LoginRequestResponse> = await axios.get(`${ADMIN_API_CONFIG.ENDPOINTS.LOGIN_REQUEST_PENDING}${queryString}`);
    return response.data;
  }

  async approveLoginRequest(requestId: number, approvedBy: string): Promise<{ success: boolean; message: string; data?: any }> {
    const data: LoginRequestApproval = { approved_by: approvedBy };
    const response: AxiosResponse<{ message: string; data?: any }> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.LOGIN_REQUEST_APPROVE(requestId), data);
    return {
      success: true,
      message: response.data.message,
      data: response.data.data
    };
  }

  async rejectLoginRequest(requestId: number, rejectedBy: string): Promise<{ success: boolean; message: string; data?: any }> {
    const data: LoginRequestApproval = { approved_by: rejectedBy };
    const response: AxiosResponse<{ message: string; data?: any }> = await axios.put(ADMIN_API_CONFIG.ENDPOINTS.LOGIN_REQUEST_REJECT(requestId), data);
    return {
      success: true,
      message: response.data.message,
      data: response.data.data
    };
  }

  async bulkApproveLoginRequests(requestIds: number[], approvedBy: string): Promise<BulkOperationResult> {
    const results = await Promise.allSettled(
      requestIds.map(id => this.approveLoginRequest(id, approvedBy))
    );
    
    const successful = results.filter(r => r.status === 'fulfilled' && r.value.success);
    const failed = results.filter(r => r.status === 'rejected' || !r.value.success);
    
    return {
      successful: successful.length,
      failed: failed.length,
      total: requestIds.length,
      details: {
        successful: successful.map(r => r.status === 'fulfilled' ? r.value : null).filter(Boolean),
        failed: failed.map(r => r.status === 'rejected' ? r.reason : (r.value as any)?.error || 'Unknown error')
      }
    };
  }

  async bulkRejectLoginRequests(requestIds: number[], rejectedBy: string): Promise<BulkOperationResult> {
    const results = await Promise.allSettled(
      requestIds.map(id => this.rejectLoginRequest(id, rejectedBy))
    );
    
    const successful = results.filter(r => r.status === 'fulfilled' && r.value.success);
    const failed = results.filter(r => r.status === 'rejected' || !r.value.success);
    
    return {
      successful: successful.length,
      failed: failed.length,
      total: requestIds.length,
      details: {
        successful: successful.map(r => r.status === 'fulfilled' ? r.value : null).filter(Boolean),
        failed: failed.map(r => r.status === 'rejected' ? r.reason : (r.value as any)?.error || 'Unknown error')
      }
    };
  }

  // ===== HELPER METHODS =====

  private buildLoginRequestQueryString(params?: LoginRequestQueryParams | { limit?: number; offset?: number }): string {
    if (!params) return '';
    
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (key === 'status' && value === 'all') {
          // Don't include 'all' status in query params
          return;
        }
        urlParams.append(key, String(value));
      }
    });
    
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildQueryString(params?: QueryParams): string {
    if (!params) return '';
    
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'object') {
          urlParams.append(key, JSON.stringify(value));
        } else {
          urlParams.append(key, String(value));
        }
      }
    });
    
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

export const adminApiService = new AdminApiService();
export default adminApiService;
